import React, { useEffect, useMemo, useState, useRef } from 'react';
import moment from 'moment';
import axios from 'axios';
import './ConsultationInvitation.css';
import 'moment/locale/de';
import {
  FiCalendar,
  FiCheck,
  FiClock,
  FiX,
} from 'react-icons/fi';

import './ScheduleConsultationModal.css';

moment.locale('de');

const buildTimes = (
  startHour,
  startMinute,
  endHour,
  endMinute
) => {
  const times = [];

  let currentMinutes = startHour * 60 + startMinute;
  const endMinutes = endHour * 60 + endMinute;

  while (currentMinutes <= endMinutes) {
    const hour = Math.floor(currentMinutes / 60);
    const minute = currentMinutes % 60;

    times.push(
      `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`
    );

    currentMinutes += 15;
  }

  return times;
};

const MORNING_TIMES = buildTimes(11, 0, 13, 45);
const AFTERNOON_TIMES = buildTimes(14, 0, 19, 0);

const normalizeTime = value => {
  const match = String(value || '').match(/(\d{1,2}):(\d{2})/);

  if (!match) return null;

  return `${String(Number(match[1])).padStart(2, '0')}:${match[2]}`;
};

// prime-ink-invitation-date-sync-v1
const syncInvitationDate = (body, previous, next) => {
  if (!previous || (previous.date === next.date && previous.time === next.time)) return body;
  const oldLabel = `${previous.date} um ${previous.time} Uhr`;
  const newLabel = `${next.date} um ${next.time} Uhr`;
  if (body.includes(oldLabel)) return body.split(oldLabel).join(newLabel);
  // A manually removed appointment line is restored with the selected appointment.
  const prefix = 'Dein vorgeschlagener Termin: ';
  const lines = body.split('\n').filter(line => !line.startsWith(prefix));
  return lines.join('\n').trimEnd() + `\n\n${prefix}${newLabel}`;
};

const ScheduleConsultationModal = ({
  open,
  activeItem,
  scheduleForm,
  setScheduleForm,
  upcomingConsultationDays = [],
  editingAppointmentId = null,
  invitation = null,
  canConvert = true,
  onConvert,
  onClose,
  onSave,
}) => {
  const [period, setPeriod] = useState('morning');
  const [showCustomTime, setShowCustomTime] = useState(false);
  const [emailSubject, setEmailSubject] = useState('Dein Beratungstermin bei Prime Ink');
  const [emailBody, setEmailBody] = useState('');
  const [emailEdited, setEmailEdited] = useState(false);
  const [expiresHours, setExpiresHours] = useState(48);
  const [candidates, setCandidates] = useState([]);
  const [originalMail, setOriginalMail] = useState(null);
  const [candidateNotice, setCandidateNotice] = useState('');
  const [sending, setSending] = useState(false);
  const [sendResult, setSendResult] = useState(null);
  const [error, setError] = useState('');
  // prime-ink-direct-booking-v1
  const [bookingMode,setBookingMode]=useState('invite');
  const [sendConfirmation,setSendConfirmation]=useState(false);
  const requestKey = useRef(null);
  const emailAppointmentRef = useRef(null);
  const invitationRef = useRef(invitation);
  invitationRef.current = invitation;
  const hasInvitation = Boolean(invitation);
  const hasSendResult = Boolean(sendResult);
  const statusId = sendResult?.id || invitation?.id;
  const isPending = Boolean(invitation && ['queued','sending','sent','uncertain'].includes(invitation.state));

  useEffect(() => {
    if (!open) return;
    requestKey.current = crypto.randomUUID();
    emailAppointmentRef.current = null;
    setEmailEdited(false); setEmailSubject('Dein Beratungstermin bei Prime Ink');
    setBookingMode('invite');setSendConfirmation(false);
    setExpiresHours(48); setOriginalMail(null); setCandidates([]);
    setCandidateNotice(''); setError(''); setSendResult(invitationRef.current || null);
  }, [open, activeItem?.id, editingAppointmentId, invitation?.id]);

  useEffect(() => {
    if (!open || !activeItem?.id || (editingAppointmentId && !hasInvitation)) return;
    const controller = new AbortController();
    setCandidateNotice('Anfrage-Mails werden gesucht …');
    axios.get(`/api/consultations/${activeItem.id}/mail-candidates`, {signal:controller.signal})
      .then(res => {
        if (controller.signal.aborted) return;
        const mails = (Array.isArray(res.data) ? [...res.data] : [])
          .sort((a, b) => {
            const timeA = Date.parse(a.date) || 0;
            const timeB = Date.parse(b.date) || 0;
            return timeB - timeA || Number(b.uid) - Number(a.uid);
          });
        setCandidates(mails);
        setOriginalMail(mails[0] || null);
        setCandidateNotice(mails.length
          ? 'Neueste Kundenmail automatisch ausgewählt. Ältere Mails kannst Du im Dropdown auswählen.'
          : 'Keine passende Mail im Posteingang gefunden. Einladung ist trotzdem möglich.');
      })
      .catch(e => {if(!controller.signal.aborted) setCandidateNotice(e.response?.data?.message || 'Postfach derzeit nicht erreichbar. Einladung ist trotzdem möglich.');});
    return () => controller.abort();
  }, [open, activeItem?.id, editingAppointmentId, invitation?.id, hasInvitation]);

  useEffect(() => {
    if (!open || !statusId) return;
    let cancelled = false;
    const poll = async () => {
      try {
        const res = await axios.get(`/api/consultation-invitations/${statusId}/status`);
        if (!cancelled) setSendResult(res.data);
      } catch { /* Keep last known state; never resend due to polling failure. */ }
    };
    void poll(); const timer = setInterval(poll, 4000);
    return () => {cancelled = true; clearInterval(timer);};
  }, [open, statusId]);

  useEffect(() => {
    if (!open) return;

    const date = upcomingConsultationDays.find(
      d => String(d.dayId) === String(scheduleForm?.dayId)
    );
    const rawName = String(activeItem?.clientName || '').trim().split(/\s+/)[0];
    const firstName = rawName
      ? rawName.charAt(0).toLocaleUpperCase('de-DE') + rawName.slice(1)
      : 'Du';

    const inquiryText = [
      activeItem?.subject,
      activeItem?.description,
    ].filter(Boolean).join(' ');

    const isCoverUp = /\bcover[\s‐‑–—-]+up\b/i.test(inquiryText);
    const dateLabel = date ? moment(date.day).format('DD.MM.YYYY') : '[Datum]';
    const timeLabel = scheduleForm?.time || '[Uhrzeit]';
    const nextAppointment = {date: dateLabel, time: timeLabel};
    const previousAppointment = emailAppointmentRef.current;
    emailAppointmentRef.current = nextAppointment;
    if (emailEdited) {
      setEmailBody(body => syncInvitationDate(body, previousAppointment, nextAppointment));
      return;
    }


    const introduction = isCoverUp
      ? 'wir haben Deine Cover-Up-Anfrage erhalten und laden Dich gerne zu einem Beratungsgespräch ins Studio ein.'
      : 'wir haben Deine Anfrage für eine Erstberatung erhalten und laden Dich gerne zu einem Beratungsgespräch ins Studio ein.';

    const consultationText = isCoverUp
      ? 'An dem Tag schauen wir uns Dein Tattoo aus der Nähe an und besprechen die Möglichkeiten für ein Cover-Up. Gemeinsam gehen wir Deine Ideen, passende Künstler, Preise und mögliche Termine durch.'
      : 'An dem Tag sprechen wir über Deine Ideen und planen gemeinsam Dein Tattoo-Projekt.';

    // prime-ink-alternative-invitation-copy-v1
    if (activeItem?.invitation_state === 'reschedule_requested') {
      setEmailBody(`Hey ${firstName},\n\nwir können Dir einen neuen Beratungstermin am ${dateLabel} um ${timeLabel} Uhr anbieten.\n\nLiebe Grüße\nPrime Ink Team`);
      return;
    }

    setEmailBody(`Hey ${firstName},\n\n${introduction}\n\nWir bieten Dir einen Termin am ${dateLabel} um ${timeLabel} Uhr an.\n\n${consultationText}\n\nLiebe Grüße\nPrime Ink Team`);
  }, [open, emailEdited, activeItem?.clientName, activeItem?.subject, activeItem?.description, activeItem?.invitation_state, scheduleForm?.dayId, scheduleForm?.time, upcomingConsultationDays]);

  useEffect(() => {
    if (open && !hasSendResult) requestKey.current = crypto.randomUUID();
  }, [open, scheduleForm?.dayId, scheduleForm?.time, emailSubject, emailBody, expiresHours, originalMail, hasSendResult]);

  const save = async (sendInvitation) => {
    if (sending) return;
    setSending(true); setError('');
    try {
      const result = await onSave(!editingAppointmentId && bookingMode==='direct' ? {directBooking:true,sendConfirmation,requestKey:requestKey.current} : sendInvitation ? {sendInvitation:true, subject:emailSubject, body:emailBody,
        expiresHours, requestKey:requestKey.current, originalMail} : {});
      if (sendInvitation && result) {
        setSendResult(result);
        if (['queued', 'sending', 'sent', 'confirmed'].includes(result.state)) {
          onClose();
        }
      }
    } catch(e) {setError(e.response?.data?.message || e.message || 'Speichern fehlgeschlagen.');}
    finally {setSending(false);}
  };

  const cancelInvitation = async () => {
    setSending(true); setError('');
    try {await axios.post(`/api/consultation-invitations/${statusId}/cancel`); await onSave({refreshOnly:true}); onClose();}
    catch(e) {setError(e.response?.data?.message || 'Stornieren fehlgeschlagen.');}
    finally {setSending(false);}
  };

  const linkOriginalMail = async () => {
    setSending(true); setError('');
    try {await axios.post(`/api/consultation-invitations/${statusId}/link-mail`,{originalMail}); setCandidateNotice('Mail zugeordnet; Synchronisierung wird wiederholt.');}
    catch(e) {setError(e.response?.data?.message || 'Zuordnung fehlgeschlagen.');}
    finally {setSending(false);}
  };


  const selectedDay = useMemo(() => {
    return upcomingConsultationDays.find(
      day =>
        String(day.dayId) ===
        String(scheduleForm?.dayId)
    );
  }, [
    upcomingConsultationDays,
    scheduleForm?.dayId,
  ]);

  const timeSlotStatus = useMemo(() => {
    const occupied = new Set();
    const current = new Set();

    if (!selectedDay) {
      return {
        occupied,
        current,
      };
    }

    const appointments = Array.isArray(selectedDay.appointments)
      ? selectedDay.appointments
      : [];

    appointments.forEach(appointment => {
      const normalizedTime = normalizeTime(
        appointment.time
      );

      if (!normalizedTime) return;

      const [hours, minutes] = normalizedTime
        .split(':')
        .map(Number);

      const startMinutes = hours * 60 + minutes;

      const duration = Math.max(15, Number(appointment.duration) || 15);

      const isCurrentAppointment =
        editingAppointmentId !== null &&
        Number(appointment.id) ===
        Number(editingAppointmentId);

      for (
        let offset = 0;
        offset < duration;
        offset += 15
      ) {
        const totalMinutes = startMinutes + offset;

        const slotHours = Math.floor(
          totalMinutes / 60
        );

        const slotMinutes = totalMinutes % 60;

        const slot = `${String(slotHours).padStart(
          2,
          '0'
        )}:${String(slotMinutes).padStart(2, '0')}`;

        if (isCurrentAppointment) {
          current.add(slot);
        } else {
          occupied.add(slot);
        }
      }
    });

    return {
      occupied,
      current,
    };
  }, [
    selectedDay,
    editingAppointmentId,
  ]);

  const occupiedTimes = timeSlotStatus.occupied;
  const currentAppointmentTimes =
    timeSlotStatus.current;

  useEffect(() => {
    if (!open) return;

    const currentTime =
      normalizeTime(scheduleForm?.time) || '11:00';

    if (MORNING_TIMES.includes(currentTime)) {
      setPeriod('morning');
      setShowCustomTime(false);
      return;
    }

    if (AFTERNOON_TIMES.includes(currentTime)) {
      setPeriod('afternoon');
      setShowCustomTime(false);
      return;
    }

    setShowCustomTime(true);
  }, [
    open,
    scheduleForm?.time,
  ]);

  if (!open || !activeItem) return null;

  const visibleTimes =
    period === 'morning'
      ? MORNING_TIMES
      : AFTERNOON_TIMES;

  const selectDay = dayId => {
    if (sending || (statusId && !editingAppointmentId) || isPending) return;
    const nextDay =
      upcomingConsultationDays.find(
        day => String(day.dayId) === String(dayId)
      );

    const appointments = Array.isArray(
      nextDay?.appointments
    )
      ? nextDay.appointments
      : [];

    const nextOccupiedTimes = new Set();

    appointments.forEach(appointment => {
      if (
        editingAppointmentId !== null &&
        Number(appointment.id) ===
        Number(editingAppointmentId)
      ) {
        return;
      }

      const appointmentTime = normalizeTime(
        appointment.time
      );

      if (!appointmentTime) return;

      const [hours, minutes] = appointmentTime
        .split(':')
        .map(Number);

      const startMinutes = hours * 60 + minutes;

     const duration = Math.max(15, Number(appointment.duration) || 15);

      for (
        let offset = 0;
        offset < duration;
        offset += 15
      ) {
        const totalMinutes = startMinutes + offset;

        const slotHours = Math.floor(
          totalMinutes / 60
        );

        const slotMinutes = totalMinutes % 60;

        nextOccupiedTimes.add(
          `${String(slotHours).padStart(
            2,
            '0'
          )}:${String(slotMinutes).padStart(2, '0')}`
        );
      }
    });

    const periodTimes =
      period === 'morning'
        ? MORNING_TIMES
        : AFTERNOON_TIMES;

    const currentTime = normalizeTime(
      scheduleForm?.time
    );

    const nextTime =
      currentTime &&
        !nextOccupiedTimes.has(currentTime)
        ? currentTime
        : periodTimes.find(
          time => !nextOccupiedTimes.has(time)
        ) || ''; 

    setScheduleForm(prev => ({
      ...prev,
      dayId: String(dayId),
      time: nextTime,
    }));
  };

  const selectPeriod = nextPeriod => {
    if (sending || (statusId && !editingAppointmentId) || isPending) return;
    const availableTimes =
      nextPeriod === 'morning'
        ? MORNING_TIMES
        : AFTERNOON_TIMES;

    setPeriod(nextPeriod);
    setShowCustomTime(false);

    const currentTime = normalizeTime(
      scheduleForm?.time
    );

    if (
      !availableTimes.includes(currentTime) ||
      occupiedTimes.has(currentTime)
    ) {
      const firstFreeTime =
        availableTimes.find(
          time => !occupiedTimes.has(time)
        ) || ''; 

      setScheduleForm(prev => ({
        ...prev,
        time: firstFreeTime,
      }));
    }
  };

  const selectTime = time => {
    if (sending || (statusId && !editingAppointmentId) || isPending) return;
    if (occupiedTimes.has(time)) return;

    setScheduleForm(prev => ({
      ...prev,
      time,
    }));
  };

  const normalizedSelectedTime = normalizeTime(
    scheduleForm?.time
  );

  const selectedTimeOccupied = (() => {
    if (!normalizedSelectedTime || !selectedDay) return false;
    const [h,m] = normalizedSelectedTime.split(':').map(Number);
    const start = h * 60 + m;
    return (selectedDay.appointments || []).some(a => {
      if (Number(a.id) === Number(editingAppointmentId)) return false;
      const t = normalizeTime(a.time); if (!t) return false;
      const [ah,am] = t.split(':').map(Number); const other = ah*60+am;
      return start < other + Math.max(15,Number(a.duration)||15) && other < start+15;
    });
  })();

  const canSave =
    Boolean(
      selectedDay && scheduleForm?.dayId &&
      normalizedSelectedTime
    ) && !selectedTimeOccupied && !sending && !isPending && (editingAppointmentId || !sendResult);

  return (
    <div
      className="schedule-tiles-overlay"
      onClick={() => {if (!sending) onClose();}}
    >
      <div
        className="schedule-tiles-modal"
        onClick={event =>
          event.stopPropagation()
        }
      >
        <button
          type="button"
          className="schedule-tiles-close"
          onClick={() => {if (!sending) onClose();}}
          aria-label="Schließen"
        >
          <FiX />
        </button>

        <div className="schedule-tiles-heading">
          <span className="schedule-tiles-icon">
            <FiCalendar />
          </span>

          <div>
            <span className="schedule-tiles-eyebrow">
              Beratung planen
            </span>

            <h3>
              {activeItem.clientName ||
                'Beratungstermin'}
            </h3>
          </div>
        </div>

        <section className="schedule-tiles-section">
          <div className="schedule-tiles-section-title">
            <span>1</span>
            Beratungstag auswählen
          </div>

          <div className="schedule-day-grid">
            {upcomingConsultationDays.length > 0 ? (
              upcomingConsultationDays.map(day => {
                const selected =
                  String(scheduleForm?.dayId) ===
                  String(day.dayId);

                const appointmentCount =
                  day.appointments?.length || 0;

                return (
                  <button
                    key={day.dayId}
                    type="button"
                    className={[
                      'schedule-day-tile',
                      selected ? 'selected' : '',
                    ]
                      .filter(Boolean)
                      .join(' ')}
                    onClick={() =>
                      selectDay(day.dayId)
                    }
                  >
                    <span className="schedule-day-weekday">
                      {moment(day.day).format(
                        'dddd'
                      )}
                    </span>

                    <strong>
                      {moment(day.day).format(
                        'DD. MMMM'
                      )}
                    </strong>

                    <small>
                      {appointmentCount}{' '}
                      {appointmentCount === 1
                        ? 'Beratung'
                        : 'Beratungen'}
                    </small>

                    {selected && <FiCheck />}
                  </button>
                );
              })
            ) : (
              <div className="schedule-tiles-empty">
                Keine kommenden Beratungstage
                vorhanden.
              </div>
            )}
          </div>
        </section>

        <section className="schedule-tiles-section">
          <div className="schedule-tiles-section-title">
            <span>2</span>
            Tageszeit auswählen
          </div>

          <div className="schedule-period-grid">
            <button
              type="button"
              className={[
                'schedule-period-tile',
                period === 'morning' &&
                  !showCustomTime
                  ? 'selected'
                  : '',
              ]
                .filter(Boolean)
                .join(' ')}
              onClick={() =>
                selectPeriod('morning')
              }
            >
              <strong>Vormittag</strong>
              <small>11:00–13:45</small>
            </button>

            <button
              type="button"
              className={[
                'schedule-period-tile',
                period === 'afternoon' &&
                  !showCustomTime
                  ? 'selected'
                  : '',
              ]
                .filter(Boolean)
                .join(' ')}
              onClick={() =>
                selectPeriod('afternoon')
              }
            >
              <strong>Nachmittag</strong>
              <small>14:00–19:00</small>
            </button>
          </div>
        </section>

        <section className="schedule-tiles-section">
          <div className="schedule-tiles-section-title">
            <span>3</span>
            Uhrzeit auswählen
          </div>

          {!showCustomTime ? (
            <div className="schedule-time-grid">
              {visibleTimes.map(time => {
                const occupied =
                  occupiedTimes.has(time);

                const current =
                  currentAppointmentTimes.has(time);

                const selected =
                  normalizedSelectedTime === time;

                return (
                  <button
                    key={time}
                    type="button"
                    className={[
                      'schedule-time-tile',
                      selected ? 'selected' : '',
                      current
                        ? 'current-appointment'
                        : '',
                      occupied ? 'occupied' : '',
                    ]
                      .filter(Boolean)
                      .join(' ')}
                    disabled={occupied}
                    onClick={() =>
                      selectTime(time)
                    }
                    title={
                      occupied
                        ? 'Bereits durch einen anderen Termin belegt'
                        : current
                          ? 'Aktueller Beratungstermin'
                          : time
                    }
                  >
                    <span>{time}</span>

                    {occupied && (
                      <small>belegt</small>
                    )}

                    {!occupied && current && (
                      <small>
                        aktueller Termin
                      </small>
                    )}
                  </button>
                );
              })}
            </div>
          ) : (
            <label className="schedule-custom-time">
              <span>Andere Uhrzeit</span>

              <input
                type="time"
                step="900"
              disabled={sending || isPending || Boolean(statusId && !editingAppointmentId)}
                value={normalizedSelectedTime || ''}
                onChange={event =>
                  setScheduleForm(prev => ({
                    ...prev,
                    time: event.target.value,
                  }))
                }
                autoFocus
              />
            </label>
          )}

          <button
            type="button"
            className="schedule-other-time"
            onClick={() =>
              setShowCustomTime(prev => !prev)
            }
          >
            <FiClock />

            {showCustomTime
              ? 'Standardzeiten anzeigen'
              : 'Andere Uhrzeit'}
          </button>
        </section>

  
      {!editingAppointmentId&&!statusId&&<>
        <div className="consultation-booking-mode" aria-label="Buchungsart">
          <button type="button" disabled={sending} aria-pressed={bookingMode==='invite'} onClick={()=>{setBookingMode('invite');requestKey.current=crypto.randomUUID();}}>Einladung senden</button>
          <button type="button" disabled={sending} aria-pressed={bookingMode==='direct'} onClick={()=>{setBookingMode('direct');requestKey.current=crypto.randomUUID();}}>Direkt buchen<br/><small>Bereits vereinbart</small></button>
        </div>
        {bookingMode==='direct'&&<div className="consultation-direct-panel">
          <p>Der Termin wird sofort bestätigt. Der Kunde muss nichts erneut bestätigen.</p>
          <label className="consultation-direct-mail"><input type="checkbox" checked={sendConfirmation} disabled={sending||!activeItem.email?.trim()} onChange={e=>{setSendConfirmation(e.target.checked);requestKey.current=crypto.randomUUID();}}/> Terminbestätigung per E-Mail senden (optional)</label>
          {!activeItem.email?.trim()&&<p>Ohne E-Mail-Adresse wird der Termin trotzdem gebucht.</p>}
        </div>}
      </>}
      {bookingMode!=='direct' && (!editingAppointmentId || invitation) && (
        <section className="consultation-email-section">
          <h4>{statusId ? 'Einladungsstatus' : '4 · E-Mail-Einladung'}</h4>
          {!statusId && <>
            <p>An: {activeItem.email || 'Keine E-Mail-Adresse hinterlegt'}</p>
            <label>Bestätigungsfrist
              <select value={expiresHours} disabled={sending} onChange={e => setExpiresHours(Number(e.target.value))}>
                {[6,12,24,48,72].map(h => <option key={h} value={h}>{h} Stunden</option>)}
              </select>
            </label>
            <p className="consultation-email-help">Der Termin ist vorläufig reserviert. Die Frist endet spätestens eine Stunde vor dem Termin. Bestätigungslink und genaue Frist werden beim Versand ergänzt.</p>
            <label>Betreff<input value={emailSubject} maxLength={200} disabled={sending} onChange={e => setEmailSubject(e.target.value)} /></label>
            <label>Nachricht<textarea value={emailBody} maxLength={10000} disabled={sending} onChange={e => {setEmailEdited(true);setEmailBody(e.target.value);}} /></label>
          </>}
          <p className="consultation-email-help">{candidateNotice}</p>
          {candidates.length > 0 && <label>Ursprüngliche Anfrage-Mail
            <select disabled={sending} value={originalMail?.uid || ''} onChange={e => setOriginalMail(candidates.find(c => String(c.uid) === e.target.value) || null)}>
              <option value="">Noch nicht zuordnen</option>
              {candidates.map(c => <option key={c.uid} value={c.uid}>{c.date ? moment(c.date).format('DD.MM. HH:mm') : ''} · {c.subject} · {c.from}</option>)}
            </select>
          </label>}
          {statusId && originalMail && <button type="button" disabled={sending} onClick={linkOriginalMail}>Anfrage-Mail zuordnen</button>}
          {sendResult && <div role="status" className="consultation-email-status">
            <strong>{{queued:'Einladung wird gesendet …',sending:'Versand läuft …',sent:'E-Mail versendet · wartet auf Bestätigung',confirmed:'Termin bestätigt',reschedule_requested:'Neuer Termin gewünscht',declined:'Vom Kunden abgesagt',expired:'Angebot abgelaufen · Uhrzeit wieder frei',failed:'Versand abgelehnt · Uhrzeit wieder frei',uncertain:'Versandstatus unklar · bitte Postfach prüfen',cancelled:'Einladung storniert'}[sendResult.state] || sendResult.state}</strong>
            {sendResult.expiresMs && <p>Frist: {new Date(sendResult.expiresMs).toLocaleString('de-DE',{timeZone:'Europe/Berlin'})}</p>}
            {sendResult.state === 'sent' && <p>{sendResult.inboxReadDone ? 'Anfrage-Mail als gelesen markiert.' : 'Postfach-Synchronisierung ausstehend.'} {sendResult.sentCopyDone ? 'Antwort in Gesendet gespeichert.' : ''}</p>}
            {sendResult.syncError && <p>{sendResult.syncError}</p>}
            {['queued','sent','uncertain','failed'].includes(sendResult.state) && <button type="button" disabled={sending} onClick={cancelInvitation}>Einladung stornieren und Uhrzeit freigeben</button>}
          </div>}
        </section>
      )}
      {!statusId && selectedTimeOccupied && <p role="alert" className="consultation-email-error">Diese Uhrzeit ist belegt.</p>}
      {error && <p role="alert" className="consultation-email-error">{error}</p>}

      <div className="schedule-tiles-summary">
          <span>
            {selectedDay
              ? moment(selectedDay.day).format(
                'dd, DD.MM.YYYY'
              )
              : 'Kein Tag gewählt'}
          </span>

          <strong>
            {normalizedSelectedTime || '–'}
          </strong>
        </div>

        <div className="schedule-tiles-actions">
          <button
            type="button"
            className="secondary"
            onClick={() => {if (!sending) onClose();}}
          >
            Abbrechen
          </button>

          {editingAppointmentId && canConvert && !isPending && onConvert && (
            <button
              type="button"
              className="convert"
              onClick={onConvert}
            >
              In Stechtermin umwandeln
            </button>
          )}

          <button
            type="button"
            className="primary"
            disabled={!canSave || (!editingAppointmentId && bookingMode!=="direct" && (!activeItem.email || !emailSubject.trim() || !emailBody.trim()))}
            onClick={() => save(!editingAppointmentId)}
          >
            <FiCheck />

            {editingAppointmentId
              ? 'Änderung speichern'
              : bookingMode==='direct' ? 'Termin direkt buchen' : 'Einladung senden'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ScheduleConsultationModal;